import React, { useEffect } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { clsx } from 'clsx';

export type SlideInToastProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  variant?: 'success' | 'error' | 'info';
  durationMs?: number; 
};

export function SlideInToast({ open, onClose, title = 'Success', message = 'Form submitted successfully.', variant = 'success', durationMs = 4500 }: SlideInToastProps) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, durationMs);
    return () => clearTimeout(t);
  }, [open, onClose, durationMs]);

  return (
    <div
      className={clsx(
        'fixed bottom-4 right-4 z-[120] transform transition-all duration-500',
        open ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none'
      )}
    >
      <div
        className={clsx(
          'w-[min(92vw,380px)] rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur-xl',
          'bg-gradient-to-br from-[#0f1c2e]/95 to-[#0a1628]/95',
          variant === 'success' && 'border-green-500/30',
          variant === 'error' && 'border-red-500/30',
          variant === 'info' && 'border-white/15'
        )}
      >
        <div className="flex items-start gap-3">
          {variant === 'success' && <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />}
          {variant === 'error' && <X className="w-5 h-5 text-red-400 mt-0.5" />}
          <div className="flex-1">
            <div className="font-montserrat font-semibold text-slate-100">{title}</div>
            {message && <div className="text-sm text-slate-300/90">{message}</div>}
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/10 transition-colors">
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      </div>
    </div>
  );
}